/* eslint-disable no-useless-escape */
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';

// eslint-disable-next-line no-control-regex
/*
 * Email pattern that validates a typical email address structure while excluding accents.
 * Explanation:
 * - ^[a-zA-Z0-9_.+-]+: Starts with one or more alphanumeric characters, dots, underscores, plus, or hyphens.
 * - @: Followed by the '@' symbol.
 * - [a-zA-Z0-9-]+: Followed by one or more alphanumeric characters or hyphens, representing the domain name.
 * - \.: Followed by a dot, which is a required part of the domain name.
 * - [a-zA-Z0-9-.]+$: Ends with one or more alphanumeric characters, dots, hyphens, or a combination, representing the top-level domain (TLD).
 */
const emailPattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

/* eslint-disable func-names */
const validations = {
  checkEmpty: function (value) {
    if (!value || value.trim().length < 1) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValue)}`
        }
      };
    }
    return null;
  },
  postalCode: function (value) {
    if (value) {
      return {
        format: {
          pattern: '^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ ][0-9][A-Z][0-9]$',
          message: `^${i18n.t(tokens.labels.validations.postalCodeValidation)}`
        }
      };
    }
    return {
      format: {
        pattern: '^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ ][0-9][A-Z][0-9]$',
        message: `^${i18n.t(tokens.labels.validations.postalCodeValidation)}`
      }
    };
  },
  creditCard: function () {
    return {
      length: {
        minimum: 16,
        message: `^${i18n.t(tokens.labels.validations.validCreditCard)}`
      }
    };
  },
  accountIdentifier: function () {
    return {
      length: {
        minimum: 6,
        message: `^${i18n.t(tokens.labels.validations.accountIdentifierInvalidLabel)}`
      }
    };
  },
  userIdentifier: function () {
    return {
      length: {
        minimum: 6,
        message: `^${i18n.t(tokens.labels.validations.userIdentifierInvalidLabel)}`
      }
    };
  },
  email: function () {
    return {
      email: {
        message: `^${i18n.t(tokens.labels.validations.validEmailAddress)}`
      },
      format: {
        pattern: emailPattern,
        message: `^${i18n.t(tokens.labels.validations.emailAddressCannotContainAccents)}`
      }
    };
  },
  contactEmail: function () {
    return {
      presence: {
        message: `^${i18n.t(tokens.labels.validations.contactEmailAddress)}`
      },
      email: {
        message: `^${i18n.t(tokens.labels.validations.validEmailAddress)}`
      },
      format: {
        pattern: emailPattern,
        message: `^${i18n.t(tokens.labels.validations.emailAddressCannotContainAccents)}`
      }
    };
  },
  companyEmail: function () {
    return {
      presence: {
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterACompanyAddress)}`
      },
      email: {
        message: `^${i18n.t(tokens.labels.validations.validEmailAddress)}`
      },
      format: {
        pattern: emailPattern,
        message: `^${i18n.t(tokens.labels.validations.emailAddressCannotContainAccents)}`
      }
    };
  },
  password: function (value) {
    if (value) {
      return {
        length: {
          minimum: 6,
          message: `^${i18n.t(tokens.labels.validations.passwordValidation)}`
        }
      };
    }
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAPassword)}`
      }
    };
  },
  subject: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.supportRequestSubjectLabel)}`
      }
    };
  },
  message: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.messageForYourNotification)}`
      }
    };
  },
  newPassword: function (value) {
    if (value) {
      return {
        format: {
          pattern: ".+",
          message: `^${i18n.t(tokens.labels.validations.yourPasswordMustBeAtLeast6Characters)}`
        }
      };
    }
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAPassword)}`
      }
    };
  },
  confirmPassword: function (value) {
    if (value) {
      return {
        format: {
          pattern: ".+",
          message: `^${i18n.t(tokens.labels.validations.yourPasswordMustBeAtLeast6Characters)}`
        }
      };
    }
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAPassword)}`
      }
    };
  },
  companyName: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyOneCompanyName)}`
            }
          };
        }

        // Return Special characters pattern
        return {
          format: {
            pattern: "[a-zA-Z0-9\\s\\'\\-\\&\\.]+",
            // working
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  company: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyOneCompanyName)}`
            }
          };
        }

        // Return Special characters pattern
        return {
          format: {
            pattern: "[a-zA-Z0-9\\s\\'\\-\\&\\.]+",
            // working
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  addressLineOne: function () {
    return {
      length: {
        minimum: 5,
        message: `^${i18n.t(tokens.labels.validations.validAddressLabel)}`
      }
    };
  },
  city: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.validMunicipalityLabel)}`
      }
    };
  },
  address: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.validAddressLabel)}`
      }
    };
  },
  number: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterValidNumber)}`
      }
    };
  },
  dollar: function () {
    return {
      length: {
        minimum: 3,
        message: `^${i18n.t(tokens.labels.validations.validDollarAmountTo2DecimalPlaces)}`
      }
    };
  },
  percentage: function () {
    return {
      presence: {
        message: `^${i18n.t(tokens.labels.validations.validPercentageNoMoreThan100to2DecimalPlaces)}`
      },
      numericality: {
        lessThanOrEqualTo: 100
      }
    };
  },
  companyAddress: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterACompanyAddress)}`
      }
    };
  },
  phone: function () {
    return {
      length: {
        minimum: 10,
        message: `^${i18n.t(tokens.labels.validations.validPhoneNumber)}`
      }
    };
  },
  description: function () {
    return {
      presence: {
        message: `^${i18n.t(tokens.labels.validations.supportRequestSubjectLabel)}`
      },
      length: {
        minimum: 3,
        message: `^${i18n.t(tokens.labels.validations.supportRequestSubjectLabel)}`
      }
    };
  },
  bankAccountHolderName: function (value) {
    // Test for length only
    if (value.length < 1) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
        }
      };
    }
    if (value) {
      // Test for Special Characters
      if (/^[0-9a-zA-Z!"#$%&'()*+,-./:;<>=?@[\]\\^_`{}|~ ]+$/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // Return Special characters pattern
        return {
          format: {
            pattern: "[a-zA-Z0-9\\s\\'\\-]+",
            // working
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  bankingInstitutionName: function (value) {
    // Test for length only
    if (value.length < 1) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
        }
      };
    }
    if (value) {
      // Test for Special Characters
      if (/^[0-9a-zA-Z!"#$%&'()*+,-./:;<>=?@[\]\\^_`{}|~ ]+$/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // Return Special characters pattern
        return {
          format: {
            pattern: "[a-zA-Z0-9\\s\\'\\-]+",
            // working
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  name: function (value) {
    // Test for length only
    if (value.length < 2) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
        }
      };
    }
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // Test for three capital letters in a row.
        if (/.+(?=(?:[A-Z]{2})|_)(?:(?:[A-Z])(?:[A-Z]\.)?)?/.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotUseSubsequentCapitalLetters)}`
            }
          };
        }

        // Return Special characters pattern and dont check for accented characters
        return {
          format: {
            pattern: "[a-zA-ZÀ-ÖØ-öø-ÿ0-9\\s\\'\\-]+",
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  birthdate: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.enterBirthDate)}`
      }
    };
  },
  className: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // eslint-disable-next-line no-useless-escape
        if (!/^[^*|\":<>[\]{}`\\()';@+%#^*!$]+$/.test(value)) {
          // Return Special characters pattern
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
            }
          };
        }
      }
    }
    return null;
  },
  classTitle: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // eslint-disable-next-line no-useless-escape
        if (!/^[^*|\":<>[\]{}`\\()';@+%#^*!$]+$/.test(value)) {
          // Return Special characters pattern
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
            }
          };
        }
      }
    }
    return null;
  },
  selectClass: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.selectClass)}`
      }
    };
  },
  divisionName: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // eslint-disable-next-line no-useless-escape
        if (!/^[^*|\":<>[\]{}`\\()';@+%#^*!$]+$/.test(value)) {
          // Return Special characters pattern
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
            }
          };
        }
      }
    }
    return null;
  },
  divisionTitle: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // eslint-disable-next-line no-useless-escape
        if (!/^[^*|\":<>[\]{}`\\()';@+%#^*!$]+$/.test(value)) {
          // Return Special characters pattern
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
            }
          };
        }
      }
    }
    return null;
  },
  selectDivision: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.selectDivision)}`
      }
    };
  },
  hireDate: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.hireDate)}`
      }
    };
  },
  maritlStat: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.maritalStatusValidation)}`
      }
    };
  },
  gender: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.genderValidation)}`
      }
    };
  },
  title: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.occupation)}`
      }
    };
  },
  coverageType: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.coverageTypeValidation)}`
      }
    };
  },
  province: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.provinceResidence)}`
      }
    };
  },
  earningsType: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.earningsType)}`
      }
    };
  },
  salary: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.annualSalary)}`
      }
    };
  },
  hourlyWage: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.hourlyWage)}`
      }
    };
  },
  hoursPerWeek: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.hoursPerWeek)}`
      }
    };
  },
  firstname: function (value) {
    // Test for length only
    if (value.length < 2) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
        }
      };
    }
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // Test for three capital letters in a row.
        if (/.+(?=(?:[A-Z]{2})|_)(?:(?:[A-Z])(?:[A-Z]\.)?)?/.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotUseSubsequentCapitalLetters)}`
            }
          };
        }

        // Return Special characters pattern and dont check for accented characters
        return {
          format: {
            pattern: "[a-zA-ZÀ-ÖØ-öø-ÿ0-9\\s\\'\\-]+",
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  lastname: function (value) {
    // Test for length only
    if (value.length < 2) {
      return {
        format: {
          // This pattern always fails. This is intentional
          pattern: "[\\s\\'\\’\\-]+",
          message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
        }
      };
    }
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\’\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\’\\-]+",
              message: `^${i18n.t(tokens.labels.validations.onlyYourOfficialName)}`
            }
          };
        }

        // Test for three capital letters in a row.
        if (/.+(?=(?:[A-Z]{2})|_)(?:(?:[A-Z])(?:[A-Z]\.)?)?/.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\’\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotUseSubsequentCapitalLetters)}`
            }
          };
        }

        // Return Special characters pattern and dont check for accented characters
        return {
          format: {
            pattern: "[a-zA-ZÀ-ÖØ-öø-ÿ0-9\\s\\'\\’\\-]+",
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  dependentFirstname: function () {
    return {
      length: {
        minimum: 3,
        // Ensures the name is longer than 2 letters
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
      },
      format: {
        pattern: /^(?!.*[A-Z]{4})[A-Za-zÀ-ÖØ-öø-ÿ'\-]+$/,
        // Ensures no more than three capital letters in a row and no special characters and allows hyphen (-) and apostrophe (')
        message: `^${i18n.t(tokens.labels.validations.subsequentCapitalsSpecialCharacters)}`
      }
    };
  },
  dependentLastname: function () {
    return {
      length: {
        minimum: 3,
        // Ensures the name is longer than 2 letters
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
      },
      format: {
        pattern: /^(?!.*[A-Z]{4})[A-Za-zÀ-ÖØ-öø-ÿ'\-]+$/,
        // Ensures no more than three capital letters in a row and no special characters and allows hyphen (-) and apostrophe (')
        message: `^${i18n.t(tokens.labels.validations.subsequentCapitalsSpecialCharacters)}`
      }
    };
  },
  relationship: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.pleaseProvideARelationship)}`
      }
    };
  },
  category: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.pleaseProvideACategory)}`
      }
    };
  },
  shares: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.pleaseProvideAPercentageOfShares)}`
      }
    };
  },
  authority: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.oneOfficialFirstAndLastName)}`
            }
          };
        }

        // Test for three capital letters in a row.
        if (/.+(?=(?:[A-Z]{2})|_)(?:(?:[A-Z])(?:[A-Z]\.)?)?/.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotUseSubsequentCapitalLetters)}`
            }
          };
        }

        // Return Special characters pattern
        return {
          format: {
            pattern: "[a-zA-ZÀ-ÖØ-öø-ÿ0-9\\s\\'\\-]+",
            // working
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return null;
  },
  authorityName: function (value) {
    if (value) {
      // Test for Special Characters
      if (/[a-zA-Z0-9\\s\\'\\-]+/.test(value)) {
        // Test for " or "
        if (/(?: or )/gi.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.oneOfficialFirstAndLastName)}`
            }
          };
        }

        // Test for three capital letters in a row.
        if (/.+(?=(?:[A-Z]{2})|_)(?:(?:[A-Z])(?:[A-Z]\.)?)?/.test(value)) {
          return {
            format: {
              // This pattern always fails. This is intentional
              pattern: "[\\s\\'\\-]+",
              message: `^${i18n.t(tokens.labels.validations.doNotUseSubsequentCapitalLetters)}`
            }
          };
        }

        // Return Special characters pattern and dont check for accented characters
        return {
          format: {
            pattern: "[a-zA-ZÀ-ÖØ-öø-ÿ0-9\\s\\'\\-]+",
            message: `^${i18n.t(tokens.labels.validations.doNotEnterAnySpecialCharacters)}`
          }
        };
      }
    }
    return {
      length: {
        minimum: 2,
        message: `^${i18n.t(tokens.labels.validations.pleaseEnterAValidName)}`
      }
    };
  },
  authorityEmail: function () {
    return {
      presence: {
        message: `^${i18n.t(tokens.labels.validations.validEmailAddress)}`
      },
      email: {
        message: `^${i18n.t(tokens.labels.validations.validEmailAddress)}`
      },
      format: {
        pattern: emailPattern,
        message: `^${i18n.t(tokens.labels.validations.emailAddressCannotContainAccents)}`
      }
    };
  },
  institutionNum: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.enterValueLabel)}`
      },
      format: {
        pattern: '^[0-9]{3}$',
        message: `^${i18n.t(tokens.labels.banking.financialInstitutionNumberValidationError)}`
      }
    };
  },
  transitNum: function () {
    return {
      length: {
        minimum: 1,
        message: `^${i18n.t(tokens.labels.validations.enterValueLabel)}`
      },
      format: {
        pattern: '^[0-9]{5}$',
        message: `^${i18n.t(tokens.labels.banking.transitNumberValidationError)}`
      }
    };
  },
  language: function () {
    return {
      format: {
        pattern: ".+",
        message: `^${i18n.t(tokens.labels.validations.selectLanguage)}`
      }
    };
  }
};
export default validations;